import { CamelCase } from '../../modules/CamelCase.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';
import { GetBlankPhoto } from '../../modules/GetBlankPhoto.js';
import { GoogleGeometry } from '../../modules/GoogleGeometry.js';
import { Round } from '../../modules/Round.js';

/** @import { KatapultJob } from '../../modules/katapult-job/KatapultJob.js' */

// Imports KPLA test cases into a job (see admin-tools/kpla-unit-test-pole-generator/README.md for the JSON format)

const FEET_TO_METERS = 0.3048;
const CREATE_METHOD = 'kpla_test_importer';

// Equipment that gets a second marker at a fixed offset from the measured one
const EQUIPMENT_MEASUREMENTS = {
  street_light: { used: 'bottom_of_bracket', other: { name: 'top_of_bracket', offset: 12 } },
  transformer: { used: 'top_bolt', other: { name: 'bottom_of_equipment', offset: -24 } }
};

// Equipment that gets a drip loop marker at a fixed offset from the measured one
const EQUIPMENT_DRIP_LOOPS = {
  street_light: { spec: 'Streetlight', offset: -4 },
  transformer: { spec: 'Secondary', offset: -28 }
};

// Equipment properties that are importer instructions rather than marker fields
const EQUIPMENT_NON_MARKER_PROPERTIES = ['measured_height', 'marker_attributes'];

// Keeps fractional inches so the height parsed back from the manual height matches the input height
export const ftIn = (inches) => Math.floor(inches / 12) + '-' + Round(inches % 12, 2);

// Scale markers to the height of the pole, based on the blank photo pole top position
const px = (height, poleTop) => [{ percentX: 50, percentY: 100 - ((82.26 * height) / poleTop + 10.5) }];

// The manual height and pixel selection of a marker measured at a height on a pole
const heightFields = (height, poleTop) => ({ manual_height: ftIn(height), pixel_selection: px(height, poleTop) });

const computeOffset = (latLng, feet, bearing) =>
  GoogleGeometry.maps.geometry.spherical.computeOffset(latLng, feet * FEET_TO_METERS, bearing);

/**
 * Apply imported attributes to a node's attributes. Plain values are imported as a single instance, objects are used as-is as a map
 * of instances, and null suppresses the attribute.
 * @param {Object} nodeAttributes - The node's attributes object (mutated)
 * @param {Object} [attributes] - The attributes to apply
 */
const applyAttributes = (nodeAttributes, attributes) => {
  for (const [prop, value] of Object.entries(attributes ?? {})) {
    nodeAttributes[prop] = value !== null && typeof value == 'object' ? value : { '-imported': value };
  }
};

/**
 * Get the parameters for creating a photo element from a flat object of marker fields. The pixel selection and manual height are pulled
 * out of the fields (they can't be set as attributes), and everything else is set as an attribute.
 * @param {string} elementType - The element's type (e.g. 'wire')
 * @param {string} id - The element's id
 * @param {Object} fields - The marker fields
 * @param {{ traceId?: string, parentId?: string }} [links] - The element's trace and parent element
 * @returns {import('../../modules/katapult-job/KatapultJobUtilities.js').PhotoElementCreateParams}
 */
export function toElementParams(elementType, id, fields, { traceId, parentId } = {}) {
  const { pixel_selection, manual_height, ...attributes } = fields;
  return {
    id,
    element_type: elementType,
    ...(pixel_selection != null && { pixel_selection }),
    ...(manual_height != null && { manual_height }),
    ...(traceId != null && { trace_id: traceId }),
    ...(parentId != null && { parent_id: parentId }),
    attributes
  };
}

/**
 * Create a blank photo to hold markers for a node or section.
 * @param {'node'|'section'} type - The kind of location the photo is for
 * @param {string|null} uid - The uid of the importing user
 * @returns {Object} The photo data
 */
export function getBlankPhoto(type, uid) {
  // Left unassociated, since associating it through the job writes both sides of the association
  return {
    ...GetBlankPhoto(type, uid ?? null),
    // Empty marker types aren't stored, but they mark the photo summary as having data
    photofirst_data: { wire: {}, insulator: {}, arm: {}, guying: {}, equipment: {}, pole_top: {} }
  };
}

/**
 * Import a list of KPLA test poles into a job. Poles are laid out along the bearings in the importer config. Items are created one at a
 * time, so a failed import leaves the cases before the failed one in the job.
 * @param {KatapultJob} job - The job to import into (should auto-cache, since the job's methods read whole collections on each call)
 * @param {Object[]} poles - The test poles to import
 * @param {Object} options
 * @param {{ attributes?: Object, map: Object }} options.config - The `kpla_test_importer` model config
 * @param {string} [options.uid] - The uid of the importing user
 * @param {() => string} [options.generateId] - Generates keys for new items
 * @param {(index: number, total: number) => void} [options.onProgress] - Called before each test case is imported
 * @returns {Promise<void>}
 */
export async function importKplaTestJob(job, poles, options) {
  const { config, uid = null, generateId = GeneratePushID, onProgress } = options;
  if (!Array.isArray(poles)) throw new Error('Expected the test case JSON to be an array of poles');

  const context = { job, config, uid, id: generateId };
  const { map } = config;
  let latLng = new GoogleGeometry.maps.LatLng(map.start_latitude, map.start_longitude);
  let direction = 'first';
  let directionCount = 0;
  let directionToggle = 0;

  for (const [index, pole] of poles.entries()) {
    onProgress?.(index, poles.length);
    // Named traces and midspan markers are shared within a case (the pole, its far poles, and its crossovers)
    context.traces = {};
    context.midspans = {};
    const scid = pole.case_id ?? ('' + index).padStart(3, '0');
    try {
      await addPole(context, pole, latLng, scid);
    } catch (error) {
      throw new Error(`Failed to import test case ${scid}: ${error.message}`, { cause: error });
    }

    directionCount++;
    if (directionCount >= map[direction + '_bearing_count']) {
      // switch direction
      direction = direction == 'first' ? 'second' : 'first';
      directionCount = 1;
      if (direction == 'first') directionToggle += 180;
    }
    latLng = computeOffset(latLng, map.distance_between, map[direction + '_bearing'] + (direction == 'second' ? 0 : directionToggle));
  }
}

/**
 * Add a pole with its blank photo, markers, spans, anchors, and pole to pole guys to the job.
 * @returns {Promise<Object>} The pole's context (ids and location) for linking other items to it
 */
async function addPole(context, pole, latLng, scid) {
  const poleContext = await addPoleNode(context, pole, latLng, scid);
  await addPoleContents(context, poleContext);
  return poleContext;
}

/**
 * Create a node with a blank main photo.
 * @returns {Promise<{ nodeId: string, photoId: string }>}
 */
async function createNodeWithPhoto(context, latLng, attributes) {
  const { job, id } = context;
  const nodeId = await job.createNode([latLng.lat(), latLng.lng()], { _key: id(), _create_method: CREATE_METHOD, attributes });
  const photoId = await job.createPhoto({ _key: id(), ...getBlankPhoto('node', context.uid) });
  await job.associatePhotoToNode(nodeId, photoId, 'main');
  return { nodeId, photoId };
}

/**
 * Add a pole node with its blank photo and pole top marker.
 * @returns {Promise<Object>} The pole's context, with no spans yet
 */
async function addPoleNode(context, pole, latLng, scid) {
  const { job, config, id } = context;
  const attributes = {
    scid: { '-imported': '' + scid },
    note: { '-imported': JSON.stringify(pole, null, 4) }
  };
  for (const prop in config.attributes) {
    attributes[prop] = { '-imported': config.attributes[prop] };
  }
  if (pole.hasOwnProperty('class')) attributes.pole_class = { '-imported': pole.class };
  if (pole.hasOwnProperty('height')) attributes.pole_height = { '-imported': pole.height };
  if (pole.hasOwnProperty('pole_spec')) attributes.pole_spec = { '-imported': pole.pole_spec };
  if (pole.hasOwnProperty('class') && pole.hasOwnProperty('height'))
    attributes.pole_spec = { '-imported': `${pole.height}-${pole.class} Southern Yellow Pine` };
  applyAttributes(attributes, pole.attributes);

  const { nodeId, photoId } = await createNodeWithPhoto(context, latLng, attributes);

  if (pole.hasOwnProperty('pole_top')) {
    const fields = {
      pole_top_extension: false,
      pixel_selection: [{ percentX: 50, percentY: 10.5 }],
      manual_height: ftIn(pole.pole_top),
      ...pole.pole_top_attributes
    };
    await job.createPhotoElement(photoId, toElementParams('pole_top', id(), fields));
  }

  return { scid, nodeId, photoId, latLng, pole, legacySpans: {}, namedSpans: {} };
}

/**
 * Add a pole's spans, markers, anchors, and pole to pole guys.
 */
async function addPoleContents(context, poleContext) {
  const { pole, scid } = poleContext;

  // Named spans, which wires can share by listing the span ids (e.g. a through wire lists a span on each side). A span ends at a
  // reference node, a far pole, or a crossover.
  for (const span of pole.spans ?? []) {
    if (poleContext.namedSpans[span.id]) throw new Error(`Duplicate span id "${span.id}" on test case ${scid}`);
    if (span.far_pole) {
      poleContext.namedSpans[span.id] = (await addFarPole(context, poleContext, span.far_pole, span, span.id)).span;
    } else if (span.crossover) {
      poleContext.namedSpans[span.id] = await addCrossover(context, poleContext, span);
    } else {
      poleContext.namedSpans[span.id] = await addReferenceSpan(context, poleContext, span.span_length, span.bearing);
    }
  }

  for (const equipment of pole.equipment ?? []) await addEquipment(context, poleContext, equipment);

  for (const type of ['arm', 'insulator', 'wire']) {
    for (const item of pole[type] ?? []) {
      if (type == 'arm' && item._children?.some((child) => child._children)) {
        await addArmWithInsulators(context, poleContext, item);
      } else if (item._children) {
        let parentMarkerId = null;
        for (const wire of item._children) {
          parentMarkerId = await addWire(context, poleContext, wire, item, parentMarkerId, type);
        }
      } else {
        await addWire(context, poleContext, item);
      }
    }
  }

  for (const guy of pole.guy ?? []) await addGuy(context, poleContext, guy);

  for (const poleToPole of pole.pole_to_pole ?? []) await addPoleToPole(context, poleContext, poleToPole);
}

async function addEquipment(context, poleContext, equipment) {
  const { job, id } = context;
  const { photoId, pole } = poleContext;
  const traceId = id();
  await job.setTraceData(traceId, { company: '', label: '', _trace_type: 'equipment' });

  const addEquipmentMarker = (fields) => job.createPhotoElement(photoId, toElementParams('equipment', id(), fields, { traceId }));

  const height = equipment.measured_height;
  const fields = equipment.hasOwnProperty('measured_height') ? heightFields(height, pole.pole_top) : {};
  for (const prop in equipment) {
    if (!EQUIPMENT_NON_MARKER_PROPERTIES.includes(prop)) fields[prop] = equipment[prop];
  }

  const measurement = EQUIPMENT_MEASUREMENTS[equipment.equipment_type];
  if (measurement) {
    fields.measurement_of = measurement.used;
    await addEquipmentMarker({
      equipment_type: equipment.equipment_type,
      measurement_of: measurement.other.name,
      ...heightFields(height + measurement.other.offset, pole.pole_top)
    });
  }

  const dripLoop = EQUIPMENT_DRIP_LOOPS[equipment.equipment_type];
  if (dripLoop) {
    await addEquipmentMarker({
      equipment_type: 'drip_loop',
      drip_loop_spec: dripLoop.spec,
      ...heightFields(height + dripLoop.offset, pole.pole_top)
    });
  }

  await addEquipmentMarker({ ...fields, ...equipment.marker_attributes });
}

/**
 * Add a cable trace for a wire. Wires with the same `trace` name in a case share one trace.
 * @returns {Promise<string>} The trace id
 */
async function addCableTrace(context, wire, hasParent) {
  if (wire.trace != null && context.traces[wire.trace]) return context.traces[wire.trace];
  const traceId = context.id();
  if (wire.trace != null) context.traces[wire.trace] = traceId;
  let cable_type = '';
  if (wire.type == 'primary/neutral' && hasParent) cable_type = 'Primary';
  else if (wire.type == 'primary/neutral' && !hasParent) cable_type = 'Neutral';
  else if (wire.type == 'communication') cable_type = 'Telco Com';
  else if (wire.type) cable_type = CamelCase(wire.type);
  await context.job.setTraceData(traceId, { company: '', label: '', cable_type, _trace_type: 'cable', ...wire.trace_attributes });
  return traceId;
}

/**
 * Add a wire marker to the pole photo (under its parent arm or insulator marker if it has one) and its midspan markers.
 * @returns {Promise<string>} The id of the top level pole marker, so sibling wires can share a parent marker
 */
async function addWire(context, poleContext, wire, parent, parentMarkerId, parentType) {
  const { job, id } = context;
  const { photoId, pole } = poleContext;
  const traceId = await addCableTrace(context, wire, !!parent);
  const markerId = parentMarkerId || id();
  if (!parentMarkerId) {
    const height = parent?.measured_height ?? wire.measured_height;
    const fields = {
      ...heightFields(height, pole.pole_top),
      arm_spec: parent?.arm_spec ?? null,
      insulator_spec: parent?.insulator_spec ?? null,
      bearing: parent?.bearing ?? null,
      ...(parent ? parent.marker_attributes : wire.marker_attributes)
    };
    // A wire without a parent is the top level marker, so it holds the trace
    await job.createPhotoElement(photoId, toElementParams(parentType || 'wire', markerId, fields, { traceId: parent ? null : traceId }));
  }
  if (parent) {
    // A child wire inherits geometry from its parent and has no pixel_selection of its own
    const childParams = toElementParams('wire', id(), { ...wire.marker_attributes }, { traceId, parentId: markerId });
    await job.createPhotoElement(photoId, childParams);
  }
  await addMidspanMarkers(context, poleContext, wire, traceId);
  return markerId;
}

/**
 * Add an arm whose children are insulators, each holding its own wires. This is the only arm structure KPLA loads wires from.
 */
async function addArmWithInsulators(context, poleContext, arm) {
  const { job, id } = context;
  const { photoId, pole } = poleContext;
  const armId = id();
  const armFields = {
    ...heightFields(arm.measured_height, pole.pole_top),
    arm_spec: arm.arm_spec ?? null,
    bearing: arm.bearing ?? null,
    ...arm.marker_attributes
  };
  await job.createPhotoElement(photoId, toElementParams('arm', armId, armFields));

  for (const insulator of arm._children) {
    const { _children: wires = [], marker_attributes, ...insulatorFields } = insulator;
    const insulatorId = id();
    const insulatorParams = toElementParams('insulator', insulatorId, { ...insulatorFields, ...marker_attributes }, { parentId: armId });
    await job.createPhotoElement(photoId, insulatorParams);

    for (const wire of wires) {
      const traceId = await addCableTrace(context, wire, true);
      const wireParams = toElementParams('wire', id(), { ...wire.marker_attributes }, { traceId, parentId: insulatorId });
      await job.createPhotoElement(photoId, wireParams);
      await addMidspanMarkers(context, poleContext, wire, traceId);
    }
  }
}

/**
 * Add a midspan marker for the wire in each of its spans. Wires list named span ids in `spans`, or are given a span from their own
 * `span_length` and `bearing` (wires with the same length and bearing share a span). `span_attributes` adds attributes to the midspan
 * marker in one named span. A trace has one midspan marker per span, so a wire on a trace that already has a marker in the span adds
 * its attributes to that marker.
 */
async function addMidspanMarkers(context, poleContext, wire, traceId) {
  const { job, id } = context;
  const { pole, scid } = poleContext;
  const spans = wire.spans
    ? wire.spans.map((spanId) => {
        const span = poleContext.namedSpans[spanId];
        if (!span) throw new Error(`Unknown span id "${spanId}" on test case ${scid}`);
        return { spanId, span };
      })
    : [{ span: await getLegacySpan(context, poleContext, wire.span_length, wire.bearing) }];

  const height = wire.midspan_height ?? wire.measured_height;
  for (const { spanId, span } of spans) {
    const attributes = { ...wire.midspan_attributes, ...wire.span_attributes?.[spanId] };
    const key = traceId + '|' + span.connId;
    const existing = context.midspans[key];
    if (existing) {
      const mergedAttributes = { ...(wire.wire_spec != null && { wire_spec: wire.wire_spec }), ...attributes };
      if (Object.keys(mergedAttributes).length) {
        await job.updatePhotoElement(existing.photoId, existing.markerId, { attributes: mergedAttributes });
      }
      continue;
    }
    const markerId = id();
    const fields = { ...heightFields(height, pole.pole_top), wire_spec: wire.wire_spec ?? null, ...attributes };
    await job.createPhotoElement(span.photoId, toElementParams('wire', markerId, fields, { traceId }));
    context.midspans[key] = { photoId: span.photoId, markerId };
  }
}

async function getLegacySpan(context, poleContext, spanLength, bearing) {
  const key = spanLength + '|' + bearing;
  poleContext.legacySpans[key] ??= await addReferenceSpan(context, poleContext, spanLength, bearing);
  return poleContext.legacySpans[key];
}

/**
 * Add a reference node at the far end of a span and a reference connection to it.
 * @returns {Promise<{ connId: string, photoId: string }>} The span's connection and midspan photo
 */
async function addReferenceSpan(context, poleContext, spanLength, bearing) {
  const { job, id } = context;
  const endpointLatLng = computeOffset(poleContext.latLng, spanLength, bearing);
  const endpointId = await job.createNode([endpointLatLng.lat(), endpointLatLng.lng()], {
    _key: id(),
    _create_method: CREATE_METHOD,
    attributes: { node_type: { '-imported': 'reference' } }
  });
  return await addConnectionWithSection(context, poleContext, { nodeId: endpointId, latLng: endpointLatLng }, 'reference');
}

/**
 * Add a connection from the pole to another node, with a midpoint section and a blank photo for midspan markers.
 * @returns {Promise<{ connId: string, photoId: string }>} The connection and its midspan photo
 */
async function addConnectionWithSection(context, poleContext, toNode, connectionType) {
  const { job, id } = context;
  const connId = await job.createConnection(poleContext.nodeId, toNode.nodeId, {
    _key: id(),
    _create_method: CREATE_METHOD,
    attributes: { connection_type: { '-imported': connectionType } }
  });
  const sectionLatLng = GoogleGeometry.maps.geometry.spherical.interpolate(poleContext.latLng, toNode.latLng, 0.5);
  const sectionId = await job.createSection(connId, [sectionLatLng.lat(), sectionLatLng.lng()], {
    _key: 'midpoint_section',
    _create_method: CREATE_METHOD
  });
  const photoId = await job.createPhoto({ _key: id(), ...getBlankPhoto('section', context.uid) });
  await job.associatePhotoToSection(sectionId, connId, photoId, 'main');
  return { connId, photoId };
}

/**
 * Add a far pole at the end of a span from the pole, connected to it by a connection with a midspan photo. When `spanId` is given, the
 * far pole knows the span by the same id, so wires on both poles can list it.
 * @returns {Promise<{ farPoleContext: Object, span: { connId: string, photoId: string } }>}
 */
async function addFarPole(context, poleContext, farPole, { bearing, span_length, connection_type }, spanId) {
  const farLatLng = computeOffset(poleContext.latLng, span_length, bearing);
  const farPoleContext = await addPoleNode(context, farPole, farLatLng, farPole.case_id ?? poleContext.scid + '-B');
  const span = await addConnectionWithSection(context, poleContext, farPoleContext, connection_type ?? 'aerial cable');
  if (spanId != null) farPoleContext.namedSpans[spanId] = span;
  await addPoleContents(context, farPoleContext);
  return { farPoleContext, span };
}

/**
 * Add a crossover node at the end of a named span. The crossover's own spans end at reference nodes, and each of its insulators is a
 * top level insulator marker holding wires (KPLA only reads crossover wires from top level insulators). The crossover knows the span
 * from the pole by the same id, so its wires can share traces with the pole's wires by `trace` name.
 * @returns {Promise<{ connId: string, photoId: string }>} The span from the pole to the crossover
 */
async function addCrossover(context, poleContext, { id: spanId, bearing, span_length, crossover }) {
  const { job, id } = context;
  const latLng = computeOffset(poleContext.latLng, span_length, bearing);
  const { nodeId, photoId } = await createNodeWithPhoto(context, latLng, { node_type: { '-imported': 'crossover' } });

  const scid = poleContext.scid + '-X';
  // Markers on the crossover are scaled to the pole's height
  const crossoverContext = {
    scid,
    nodeId,
    photoId,
    latLng,
    pole: { pole_top: poleContext.pole.pole_top },
    legacySpans: {},
    namedSpans: {}
  };
  const span = await addConnectionWithSection(context, poleContext, crossoverContext, crossover.connection_type ?? 'aerial cable');
  crossoverContext.namedSpans[spanId] = span;
  for (const crossoverSpan of crossover.spans ?? []) {
    if (crossoverContext.namedSpans[crossoverSpan.id]) throw new Error(`Duplicate span id "${crossoverSpan.id}" on crossover ${scid}`);
    crossoverContext.namedSpans[crossoverSpan.id] = await addReferenceSpan(
      context,
      crossoverContext,
      crossoverSpan.span_length,
      crossoverSpan.bearing
    );
  }

  for (const insulator of crossover.insulators ?? []) {
    const { _children: wires = [], marker_attributes, measured_height, ...insulatorFields } = insulator;
    const insulatorId = id();
    const fields = { ...heightFields(measured_height, crossoverContext.pole.pole_top), ...insulatorFields, ...marker_attributes };
    await job.createPhotoElement(photoId, toElementParams('insulator', insulatorId, fields));
    for (const wire of wires) {
      const traceId = await addCableTrace(context, wire, true);
      const wireParams = toElementParams('wire', id(), { ...wire.marker_attributes }, { traceId, parentId: insulatorId });
      await job.createPhotoElement(photoId, wireParams);
      await addMidspanMarkers(context, crossoverContext, wire, traceId);
    }
  }
  return span;
}

/**
 * Add an anchor node at the end of a guy's lead and a connection from the pole to it.
 * @returns {Promise<string>} The anchor node id
 */
async function addAnchor(context, poleContext, { bearing, lead, nodeType, anchorSpec, soilType, anchorAttributes, connectionType }) {
  const { job, id } = context;
  const anchorLatLng = computeOffset(poleContext.latLng, lead, bearing);
  const attributes = {
    node_type: { '-imported': nodeType },
    anc_elevation: { '-imported': '0' },
    existing_aux_eye: { '-imported': '0' },
    eyes: { '-imported': '' },
    size: { '-imported': '' },
    sizes_of_attached_dn_guys: { '-imported': '' },
    anchor_spec: { '-imported': anchorSpec ?? null },
    soil_type: { '-imported': soilType ?? null }
  };
  applyAttributes(attributes, anchorAttributes);
  const anchorId = await job.createNode([anchorLatLng.lat(), anchorLatLng.lng()], {
    _key: id(),
    _create_method: CREATE_METHOD,
    attributes
  });

  await job.createConnection(poleContext.nodeId, anchorId, {
    _key: id(),
    _create_method: CREATE_METHOD,
    attributes: { connection_type: { '-imported': connectionType } }
  });
  return anchorId;
}

/**
 * Add a down guy (optionally with a sidewalk brace and a proposed anchor) or a pushbrace.
 */
async function addGuy(context, poleContext, guy) {
  const { job, id } = context;
  const { photoId, pole } = poleContext;
  const guyingType = guy.guying_type;
  const isPushbrace = guyingType == 'pushbrace';
  const anchorId = await addAnchor(context, poleContext, {
    bearing: guy.bearing,
    lead: guy.lead,
    nodeType: 'existing anchor',
    anchorSpec: guy.anchor_spec,
    soilType: guy.soil_type,
    anchorAttributes: guy.anchor_attributes,
    connectionType: isPushbrace ? 'pushbrace' : 'down guy'
  });

  // The guy marker links to its proposed anchor and brace, so those ids are known before the marker is created
  const proposedAnchor = !isPushbrace ? guy.proposed_anchor : null;
  const proposedAnchorId = proposedAnchor
    ? await addAnchor(context, poleContext, {
        bearing: proposedAnchor.bearing,
        lead: proposedAnchor.lead,
        nodeType: 'new anchor',
        anchorSpec: proposedAnchor.anchor_spec,
        soilType: proposedAnchor.soil_type,
        anchorAttributes: proposedAnchor.anchor_attributes,
        connectionType: 'down guy'
      })
    : null;
  const brace = !isPushbrace ? guy.brace : null;
  const braceMarkerId = brace ? id() : null;

  const traceId = id();
  await job.setTraceData(traceId, { company: '', label: '', _trace_type: 'down_guy' });

  const fields = {
    ...heightFields(guy.measured_height, pole.pole_top),
    ...(isPushbrace ? { pushbrace_spec: guy.pushbrace_spec ?? null } : { wire_spec: guy.wire_spec ?? null }),
    guying_type: guyingType ?? null,
    anchor_id: anchorId,
    ...(braceMarkerId && { sidewalk_brace_id: braceMarkerId }),
    ...(proposedAnchorId && { proposed_anchor_id: proposedAnchorId }),
    ...guy.marker_attributes
  };
  await job.createPhotoElement(photoId, toElementParams('guying', id(), fields, { traceId }));

  if (brace) {
    const braceFields = {
      ...heightFields(brace.measured_height, pole.pole_top),
      sidewalk_brace_spec: brace.sidewalk_brace_spec ?? null,
      sidewalk_brace_id: braceMarkerId,
      guying_type: 'sidewalk brace'
    };
    await job.createPhotoElement(photoId, toElementParams('guying', braceMarkerId, braceFields, { traceId }));
  }
}

/**
 * Add a far pole and one or more pole to pole guys spanning to it. Each guy is a wire marker on both poles (sharing a 'Guy' trace)
 * plus a midspan marker with its wire spec, and `point_load_end` marks the end that takes the other pole's guy load as a point load.
 */
async function addPoleToPole(context, poleContext, poleToPole) {
  const { job, id } = context;
  const farPole = poleToPole.far_pole;
  if (!farPole) throw new Error(`Pole to pole guy on test case ${poleContext.scid} is missing a far_pole`);
  const { farPoleContext, span } = await addFarPole(context, poleContext, farPole, poleToPole);

  for (const guy of poleToPole.guys ?? []) {
    const traceId = id();
    await job.setTraceData(traceId, { company: '', label: '', cable_type: 'Guy', _trace_type: 'cable', ...guy.trace_attributes });

    const ends = [
      { end: 'near', endContext: poleContext, height: guy.near_height, markerAttributes: guy.near_marker_attributes },
      { end: 'far', endContext: farPoleContext, height: guy.far_height, markerAttributes: guy.far_marker_attributes }
    ];
    for (const { end, endContext, height, markerAttributes } of ends) {
      const fields = {
        ...heightFields(height, endContext.pole.pole_top),
        point_load_end: guy.point_load_end == end ? true : null,
        ...markerAttributes
      };
      await job.createPhotoElement(endContext.photoId, toElementParams('wire', id(), fields, { traceId }));
    }

    const midspanHeight = guy.midspan_height ?? (guy.near_height + guy.far_height) / 2;
    const midspanFields = {
      ...heightFields(midspanHeight, poleContext.pole.pole_top),
      wire_spec: guy.wire_spec ?? null,
      ...guy.midspan_attributes
    };
    await job.createPhotoElement(span.photoId, toElementParams('wire', id(), midspanFields, { traceId }));
  }
}
